import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Button, Card, ConfirmDialog, DataGrid, SearchableSelect, StatusBadge, useToast,
  type DataGridColumn, type SearchableSelectOption,
} from '@kss/ui';
import { ArrowLeft, Pencil, Plus, RefreshCw, Save, Trash2 } from 'lucide-react';
import { encodeId, decodeId } from '../../utils/idCodec';
import { ImageUploadField } from '../api-browser/GenericCrudForm';
import { persistImage } from '../../utils/persistImage';

/* ----------------------------- CrudList ----------------------------- */

export interface CrudListProps<T, _C, _F> {
  title: string;
  newLabel: string;
  basePath: string;
  /** PK field on the row (e.g. "tenantId"). */
  idKey: keyof T & string;
  /** Field to show in confirm-delete dialog. */
  labelKey: keyof T & string;
  service: {
    all: () => Promise<T[]>;
    delete: (id: string) => Promise<void>;
  };
  columns: DataGridColumn<T>[];
}

export function CrudList<T extends object, C, F>({
  title,
  newLabel,
  basePath,
  idKey,
  labelKey,
  service,
  columns,
}: CrudListProps<T, C, F>) {
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast); toastRef.current = toast;

  const [rows, setRows] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<T | null>(null);
  const [deleting, setDeleting] = useState(false);

  async function load() {
    setLoading(true); setErr(null);
    try {
      const data = await service.all();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to load';
      setErr(msg); toastRef.current.error('Load failed', msg);
    } finally { setLoading(false); }
  }
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  async function onDelete() {
    // G60 / §35.3: backend's DELETE only binds to numeric `id`. The
    // configured `idKey` is often `code` per the new swagger (string),
    // which routes to /{code} → 404. Prefer the numeric `id` whenever
    // the row exposes it.
    const row = confirmDelete as Record<string, unknown> | null | undefined;
    const id = String(row?.id ?? row?.[idKey] ?? '');
    if (!id) return;
    setDeleting(true);
    try {
      await service.delete(id);
      toast.success('Deleted', `${String(confirmDelete?.[labelKey] ?? 'Item')} removed`);
      setConfirmDelete(null);
      await load();
    } catch (e) {
      toast.error('Delete failed', e instanceof Error ? e.message : 'Failed');
    } finally { setDeleting(false); }
  }

  const enrichedColumns: DataGridColumn<T>[] = [
    ...columns,
    {
      accessor: idKey,
      title: 'Actions',
      width: 120,
      render: (r: T) => {
        const id = r[idKey] as string | undefined;
        return (
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" onClick={() => id && navigate(`${basePath}/${encodeId(id)}`)} title="Edit">
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(r)} title="Delete">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="-m-6 p-[3px]">
      {err && <Alert variant="danger" className="mb-3">{err}</Alert>}
      <DataGrid<T>
        title={<span className="hidden sm:inline">{title}</span>}
        data={rows}
        columns={enrichedColumns}
        defaultPageSize={10}
        pageSizes={[5, 10, 25, 50, 100]}
        toolbar={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Link to={`${basePath}/new`}>
              <Button size="sm"><Plus className="h-3.5 w-3.5" /> {newLabel}</Button>
            </Link>
          </div>
        }
      />
      <ConfirmDialog
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={onDelete}
        title="Delete?"
        message={`Permanently remove "${String(confirmDelete?.[labelKey] ?? '')}"? This cannot be undone.`}
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
        variant="danger"
      />
    </div>
  );
}

/* ----------------------------- CrudForm ----------------------------- */

export type FieldType = 'text' | 'email' | 'tel' | 'url' | 'number' | 'select' | 'search' | 'status' | 'image';

export interface CrudFormField<F> {
  key: keyof F & string;
  label: string;
  type?: FieldType;
  required?: boolean;
  placeholder?: string;
  /** for select */
  options?: { value: string; label: string }[];
  /** for search (async-loaded SearchableSelect options) */
  loadOptions?: () => Promise<SearchableSelectOption[]>;
  /** Render across BOTH columns (its own full-width row). Use for
   *  long text, image uploads, JSON, etc. */
  fullWidth?: boolean;
}

export interface CrudFormProps<T, C, _F, Form> {
  /** Lowercase entity name, e.g. "tenant". Used in title and toasts. */
  title: string;
  basePath: string;
  idKey: keyof T & string;
  service: {
    get: (id: string) => Promise<T>;
    create: (items: C[]) => Promise<T[]>;
    update: (items: T[]) => Promise<T[]>;
  };
  emptyForm: Form;
  fields: CrudFormField<Form>[];
  /** Map a fetched entity to form state (edit mode). */
  fromEntity: (r: T) => Form;
  /** Map form state to a saveable entity payload. `id` undefined on create. */
  toEntity: (f: Form, id?: string) => T;
  /** Return an error string, or null if valid. */
  validate?: (f: Form) => string | null;
}

const inputCls =
  'h-10 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';
const selectCls =
  'h-10 w-full appearance-none rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';

export function CrudForm<T extends object, C, F, Form extends Record<string, unknown>>({
  title,
  basePath,
  idKey,
  service,
  emptyForm,
  fields,
  fromEntity,
  toEntity,
  validate,
}: CrudFormProps<T, C, F, Form>) {
  const { id: rawId } = useParams<{ id?: string }>();
  const id = decodeId(rawId);
  const isEdit = !!id && id !== 'new';
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast); toastRef.current = toast;

  const [form, setForm] = useState<Form>(emptyForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (isEdit && id) {
        setLoading(true); setErr(null);
        try {
          const r = await service.get(id);
          if (!cancelled) setForm(fromEntity(r));
        } catch (e) {
          const msg = e instanceof Error ? e.message : 'Not found';
          setErr(msg); toastRef.current.error('Load failed', msg);
        } finally { if (!cancelled) setLoading(false); }
      } else setForm(emptyForm);
    }
    void run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  function set<K extends keyof Form>(k: K, v: Form[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function save() {
    if (validate) {
      const e = validate(form);
      if (e) { toast.error('Fix errors', e); return; }
    }
    setSaving(true);
    try {
      // Persist any image fields: upload data: URLs to /api/Image/upload
      // and swap the inline base64 for the hosted URL before saving.
      let working = form;
      const imageFields = fields.filter((f) => f.type === 'image');
      if (imageFields.length) {
        const copy = { ...form } as Record<string, unknown>;
        for (const f of imageFields) {
          const v = copy[f.key];
          if (typeof v === 'string' && v.startsWith('data:')) {
            try { copy[f.key] = (await persistImage(v)) ?? v; } catch { /* keep data URL on upload failure */ }
          }
        }
        working = copy as Form;
      }
      // §35: the API expects `isActive` as a NUMBER (0/1) and `status`
      // as the numeric row enum. A boolean `isActive` (what the status
      // dropdown produces) 400s with invalid_body — coerce it. Derive a
      // numeric `status` from it too when the entity didn't set one.
      const normalize = (raw: T): T => {
        const e = raw as Record<string, unknown>;
        if (typeof e.isActive === 'boolean') {
          const n = e.isActive ? 1 : 0;
          e.isActive = n;
          if (e.status == null || typeof e.status === 'string') e.status = n === 1 ? 0 : 1;
        }
        return e as T;
      };
      if (isEdit && id) {
        await service.update([normalize(toEntity(working, id) as T)]);
        toast.success('Saved', `${title.charAt(0).toUpperCase() + title.slice(1)} updated`);
      } else {
        await service.create([normalize(toEntity(working) as T) as unknown as C]);
        toast.success('Saved', `${title.charAt(0).toUpperCase() + title.slice(1)} added`);
      }
      navigate(basePath);
    } catch (e) {
      toast.error('Save failed', e instanceof Error ? e.message : 'Failed');
    } finally { setSaving(false); }
  }

  function renderField(f: CrudFormField<Form>): ReactNode {
    const val = form[f.key] as unknown;
    const labelEl = (
      <span className="text-xs font-medium text-content-secondary">
        {f.label}{f.required && <span className="text-danger"> *</span>}
      </span>
    );

    if (f.type === 'status') {
      return (
        <label key={String(f.key)} className="flex flex-col gap-1">
          {labelEl}
          <select
            className={selectCls}
            value={val ? 'active' : 'inactive'}
            onChange={(e) => set(f.key, (e.target.value === 'active') as Form[typeof f.key])}
          >
            <option value="active">active</option>
            <option value="inactive">inactive</option>
          </select>
        </label>
      );
    }

    if (f.type === 'select') {
      return (
        <label key={String(f.key)} className="flex flex-col gap-1">
          {labelEl}
          <select
            className={selectCls}
            value={String(val ?? '')}
            onChange={(e) => set(f.key, e.target.value as Form[typeof f.key])}
          >
            <option value="">—</option>
            {f.options?.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
      );
    }

    if (f.type === 'search') {
      return (
        <div key={String(f.key)} className="flex flex-col gap-1">
          {labelEl}
          <SearchableSelect
            value={String(val ?? '')}
            onChange={(v) => set(f.key, v as Form[typeof f.key])}
            loadOptions={f.loadOptions}
            placeholder={f.placeholder ?? 'Select…'}
            searchPlaceholder="Search…"
          />
        </div>
      );
    }

    if (f.type === 'image') {
      return (
        <div key={String(f.key)} className="flex flex-col gap-1">
          {labelEl}
          <ImageUploadField
            value={val}
            onChange={(v) => set(f.key, (v ?? '') as Form[typeof f.key])}
            multiple={false}
          />
        </div>
      );
    }

    const isNum = f.type === 'number';
    return (
      <label key={String(f.key)} className="flex flex-col gap-1">
        {labelEl}
        <input
          type={f.type === 'email' ? 'email' : f.type === 'tel' ? 'tel' : f.type === 'url' ? 'url' : isNum ? 'number' : 'text'}
          className={inputCls}
          value={val == null ? '' : String(val)}
          placeholder={f.placeholder}
          onChange={(e) => {
            const raw = e.target.value;
            const next = (isNum ? (raw === '' ? '' : Number(raw)) : raw) as unknown as Form[typeof f.key];
            set(f.key, next);
          }}
        />
      </label>
    );
  }

  return (
    <div className="-m-6 p-[3px]">
      {err && <Alert variant="danger" className="mb-3">{err}</Alert>}
      <Card>
        <div className="mb-3 flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate(basePath)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-base font-semibold text-content-primary">
            {isEdit ? `Edit ${title}` : `New ${title}`}
          </h2>
          {isEdit && <StatusBadge status="active" />}
        </div>
        {loading ? (
          <div className="text-sm text-content-muted">Loading…</div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {fields.map((f) => (
              <div key={String(f.key)} className={f.fullWidth ? 'sm:col-span-2' : ''}>
                {renderField(f)}
              </div>
            ))}
          </div>
        )}
        <div className="mt-4 flex items-center justify-end gap-2 border-t border-border pt-4">
          <Button variant="outline" onClick={() => navigate(basePath)} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving || loading}>
            <Save className="h-4 w-4" /> {saving ? 'Saving…' : isEdit ? 'Update' : 'Save'}
          </Button>
        </div>
      </Card>
    </div>
  );
}
